import { InterestedSellerDialog } from 'components/interested-seller-dialog';
import { Card, ErrorMessage, Icon, IconButton, Tag, Text } from 'components/ui';
import { format } from 'date-fns';
import { useDialogToggle } from 'hooks/use-dialog-toggle';
import { useEnquiryList } from 'hooks/use-enquiry-list';
import { ListingEnquiry } from 'lib/api/generated';
import { CALLBACK_REQUEST, OFFER_RECEIVED } from 'lib/helpers';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { breakpoints } from 'styles/breakpoints';
import { globalTheme } from 'styles/global-theme';

type Props = {
  listingId: string;
};

const ListWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${globalTheme.space[1]};
  @media screen and (max-width: ${breakpoints.medium}) {
    grid-template-columns: 1fr;
  }
`;

const List = styled(Card)`
  padding: ${globalTheme.space[1]};
  display: grid;
  flex: 1;
  width: 100%;
  grid-template-columns: 1fr max-content;
  align-items: center;
  cursor: pointer;
  @media screen and (max-width: ${breakpoints.medium}) {
    background-color: white;
    border: 1px solid ${globalTheme.colors.lightGray};
  }
`;
const ViewAllButton = styled(IconButton)`
  margin-top: 20px;
  align-self: end;
`;

const ListContent = styled.div<{ $flexDirection?: 'row' | 'column' }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  ${Tag} {
    margin-right: ${globalTheme.space[5]};
  }

  ${Icon} {
    cursor: pointer;
  }
  @media screen and (max-width: ${breakpoints.medium}) {
    ${Tag} {
      margin-right: ${globalTheme.space[1]};
      min-width: unset;
    }
  }
  @media screen and (max-width: ${breakpoints.large}) {
    flex-direction: ${({ $flexDirection }) => $flexDirection && $flexDirection};
  }
`;

const EOIList = ({ listingId }: Props) => {
  const [viewMore, setViewMore] = useState<boolean>(false);
  const [enquiryId, setEnquiryId] = useState<string>();

  const { data, isLoading, isError, refetch } = useEnquiryList({
    listingId,
    pageSize: viewMore ? 1000 : 10,
  });

  const [enquiryDialogOpen, openEnquiryDialog, closeEnquiryDialog] =
    useDialogToggle(false);

  useEffect(() => {
    refetch();
  }, [viewMore, refetch]);

  if (isLoading) {
    return <Text>Loading EOIs...</Text>;
  }

  if (isError)
    return <ErrorMessage>Error fetching interested sellers</ErrorMessage>;

  if (data.count === 0) {
    return <Text>No outstanding EOIs</Text>;
  }

  return (
    <>
      <ListWrapper>
        {data &&
          !!data.results.length &&
          data.results.map(
            (
              {
                name,
                listing_enquiry_id,
                read_timestamp,
                created,
                message,
              }: ListingEnquiry,
              idx: number
            ) => (
              <List
                key={listing_enquiry_id}
                onClick={() => {
                  setEnquiryId(listing_enquiry_id);
                  openEnquiryDialog();
                }}
              >
                <ListContent $flexDirection='column'>
                  <Text fontWeight='semiBold'>{name || 'Unknown User'}</Text>
                  <Text>
                    {' '}
                    {format(new Date(created), 'K:mm a, eee, MMM dd ')}
                  </Text>
                </ListContent>
                <ListContent>
                  {!read_timestamp && (
                    <Tag color='white' backgroundColor='red'>
                      New
                    </Tag>
                  )}
                  {message.includes(OFFER_RECEIVED) && (
                    <Tag color='darkBlue' backgroundColor='lightBlue'>
                      Offer
                    </Tag>
                  )}

                  {!message.includes(OFFER_RECEIVED) &&
                    !message.includes(CALLBACK_REQUEST) && (
                      <Tag color='green' backgroundColor='lightGreen'>
                        Enquiry
                      </Tag>
                    )}

                  {message.includes(CALLBACK_REQUEST) && (
                    <Tag color='orange' backgroundColor='yellow'>
                      Callback
                    </Tag>
                  )}

                  <div
                    onClick={() => {
                      openEnquiryDialog();
                      setEnquiryId(listing_enquiry_id);
                    }}
                  >
                    <Icon component='ChevronNextIcon' />
                  </div>
                </ListContent>
              </List>
            )
          )}
      </ListWrapper>
      {data.count > 10 && (
        <ViewAllButton
          textLeft
          icon={viewMore ? 'ArrowUpIcon' : 'ArrowRightIcon'}
          text={viewMore ? 'View less' : 'View All'}
          buttonColor='aqua'
          disabled={isLoading}
          size={2}
          onClick={() => {
            setViewMore(!viewMore);
          }}
        />
      )}
      <InterestedSellerDialog
        isOpen={enquiryDialogOpen}
        onDismiss={() => {
          refetch();
          closeEnquiryDialog();
        }}
        enquiryId={enquiryId}
        listingId={listingId}
      />
    </>
  );
};

export default EOIList;
